import { useEffect, useRef } from "react";

/** What slides in as it scrolls into view — the design's own list. */
const TARGETS =
  ".section .kicker,.section .h2,.section .lead,.step,.op,.feat,.compare,.faq,.calc,.tl-node,.code-card,.final-inner>*";

/** After this long everything is shown, whatever the observer did. */
const FALLBACK_MS = 1400;

/**
 * Scroll-in reveal from the „Forgmatic Platforma v6" design (KAN-1329).
 *
 * Decoration only, so it fails open: nothing is hidden until this effect has
 * run, a background tab and reduced motion skip it, and a fallback timer shows
 * whatever the observer has not reached yet. Content that mounts later (the
 * live operation list) is simply never hidden.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (
      document.visibilityState !== "visible" ||
      !("IntersectionObserver" in window) ||
      typeof window.matchMedia !== "function" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const targets = Array.from(root.querySelectorAll<HTMLElement>(TARGETS));
    targets.forEach((el) => el.classList.add("rv"));
    targets.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.95) {
        el.classList.add("in");
      }
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          const siblings = Array.from(el.parentElement?.children ?? []).filter(
            (c) => c.classList.contains("rv"),
          );
          el.style.transitionDelay = `${Math.min(siblings.indexOf(el), 6) * 60}ms`;
          el.classList.add("in");
          io.unobserve(el);
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.08 },
    );
    targets.forEach((el) => io.observe(el));

    const fallback = window.setTimeout(() => {
      targets.forEach((el) => el.classList.add("in"));
    }, FALLBACK_MS);

    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
      targets.forEach((el) => el.classList.remove("rv", "in"));
    };
  }, []);

  return ref;
}
